import { useState } from "react";
import { BookOpen, ChevronRight, ChevronDown, ExternalLink } from "lucide-react";

const StatuteDetailsSection = ({ config, stateName, violationType }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showVerbose, setShowVerbose] = useState(false);
  
  return (
    <div className="rounded-xl border border-blue-900/50 bg-blue-950/20 overflow-hidden">
      {/* Header - Always Visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-blue-900/20 transition-colors"
        data-testid="statute-details-toggle"
      >
        <div className="flex items-center gap-3">
          <BookOpen className="w-5 h-5 text-blue-400" />
          <div className="text-left">
            <h4 className="font-semibold text-blue-300">State Statute Details</h4>
            <p className="text-xs text-slate-400">
              {stateName} - {violationType} - {config.statute_of_limitations_years || 'N/A'} year statute of limitations
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-blue-400 font-medium">
            {isExpanded ? 'Hide' : 'View'} Legal Reference
          </span>
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-blue-400" />
          ) : (
            <ChevronRight className="w-4 h-4 text-blue-400" />
          )}
        </div>
      </button>
      
      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-4 pb-4 space-y-4 border-t border-blue-900/50">
          {/* Statute Reference */}
          {config.statute_reference && (
            <div className="pt-4">
              <p className="text-xs font-semibold text-blue-400 uppercase tracking-wide mb-1">
                Legal Citation
              </p>
              <p className="text-sm text-white font-mono bg-slate-800 px-3 py-2 rounded-lg">
                {config.statute_reference}
              </p>
            </div>
          )}
          
          {/* Summary */}
          {config.statute_summary && (
            <div>
              <p className="text-xs font-semibold text-blue-400 uppercase tracking-wide mb-1">
                Summary
              </p>
              <p className="text-sm text-slate-300 leading-relaxed">
                {config.statute_summary}
              </p>
            </div>
          )}
          
          {/* Verbose - Hidden by Default */}
          {config.statute_verbose && (
            <div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowVerbose(!showVerbose);
                }}
                className="flex items-center gap-2 text-xs text-blue-400 hover:text-blue-300 transition-colors mb-2"
                data-testid="statute-verbose-toggle"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                {showVerbose ? 'Hide' : 'Show'} Full Legal Text
              </button>
              {showVerbose && (
                <div className="p-4 bg-slate-900 border border-slate-700 rounded-lg">
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">
                    Full Legal Text
                  </p>
                  <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-wrap">
                    {config.statute_verbose}
                  </p>
                </div>
              )}
            </div>
          )}
          
          {/* Key Info Grid */}
          <div className="grid grid-cols-2 gap-3 pt-2">
            <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700">
              <p className="text-xs text-slate-400">Statute of Limitations</p>
              <p className="text-lg font-bold text-white">
                {config.statute_of_limitations_years || 'N/A'} {config.statute_of_limitations_years === 1 ? 'year' : 'years'}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700">
              <p className="text-xs text-slate-400">State</p>
              <p className="text-lg font-bold text-white">{stateName}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StatuteDetailsSection;
